import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import {
  Check, ChevronRight, ChevronLeft, Clock, MapPin, Music2, Phone, PartyPopper, Users, Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EVENT_TYPES = [
  { value: 'birthday', label: 'Birthday Party', icon: PartyPopper },
  { value: 'wedding', label: 'Wedding', icon: Sparkles },
  { value: 'engagement', label: 'Engagement Party', icon: Sparkles },
  { value: 'baptism', label: 'Baptism', icon: Users },
  { value: 'confirmation', label: 'Confirmation', icon: Users },
  { value: 'communion', label: 'Communion', icon: Users },
  { value: 'civil_wedding', label: 'Civil Wedding', icon: Sparkles },
  { value: 'first_birthday', label: '1st Birthday', icon: PartyPopper },
  { value: '21st_birthday', label: '21st Birthday', icon: PartyPopper },
  { value: '50th_birthday', label: '50th Birthday', icon: PartyPopper },
  { value: 'silver_wedding', label: 'Silver Wedding', icon: Sparkles },
  { value: 'golden_wedding', label: 'Golden Wedding', icon: Sparkles },
  { value: 'house_warming', label: 'House Warming', icon: Users },
  { value: 'corporate', label: 'Corporate Event', icon: Users },
  { value: 'private_party', label: 'Private Party', icon: Music2 },
  { value: 'community', label: 'Community Event', icon: Users },
  { value: 'religious', label: 'Religious Gathering', icon: Users },
  { value: 'other', label: 'Other', icon: Music2 },
];

const FALLBACK_PACKAGES = [
  { tier: 'basic', name: 'Basic Package', price: 250, duration: 'Up to 6 Hours', description: 'Includes sound, complimentary toast song music, standard smoke machine LED, DJ Lights', features: ['Professional DJ', 'Up to 6 hours', 'Pro sound system', 'Smoke machine & LED', 'DJ Lights'] },
  { tier: 'standard', name: 'Standard Package', price: 300, duration: 'Up to 8 Hours', description: 'Includes sound, complimentary toast song music, standard smoke machine LED, DJ Lights', features: ['Professional DJ', 'Up to 8 hours', 'Pro sound system', 'Smoke machine & LED', 'DJ Lights', 'Requests welcomed'], highlight: true },
];

const MUSIC_GENRES = [
  'Afrobeats', 'Goan Konkani', 'Hip-Hop', 'House', 'UK Hip-Hop', 'Dance', 'Reggaeton / Latin', 'Bollywood Bangers'
];

export default function DJBooking() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [packages, setPackages] = useState(FALLBACK_PACKAGES);
  const [eventType, setEventType] = useState('');
  const [customEventType, setCustomEventType] = useState('');
  const [packageTier, setPackageTier] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [location, setLocation] = useState('');
  const [venue, setVenue] = useState('');
  const [expectedGuests, setExpectedGuests] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [musicPreferences, setMusicPreferences] = useState([]);
  const [specialRequest, setSpecialRequest] = useState('');
  const [letDJChoose, setLetDJChoose] = useState(false);
  const [firstDanceSong, setFirstDanceSong] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/packages/dj`).then(res => {
      if (res.data.packages?.length > 0) setPackages(res.data.packages);
    }).catch(() => {});
  }, []);

  const selectedPackage = packages.find(p => p.tier === packageTier);

  const canProceed = () => {
    switch (step) {
      case 1: return eventType !== '' && (eventType !== 'other' || customEventType.trim() !== '');
      case 2: return packageTier !== '';
      case 3: return selectedDate !== null && startTime !== '' && phoneNumber.trim() !== '';
      default: return false;
    }
  };

  const toggleGenre = (genre) => {
    setMusicPreferences(prev =>
      prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
    );
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to book');
      navigate('/login?redirect=/dj/book&type=dj');
      return;
    }
    setSubmitting(true);
    try {
      const price = selectedPackage?.price || 0;
      await axios.post(`${API}/dj/bookings`, {
        event_type: eventType === 'other' ? customEventType : eventType,
        package_tier: packageTier,
        event_date: selectedDate?.toISOString().split('T')[0],
        start_time: startTime,
        end_time: endTime,
        location,
        venue,
        expected_guests: expectedGuests,
        phone_number: phoneNumber,
        music_preferences: musicPreferences,
        special_request: specialRequest,
        let_dj_choose: letDJChoose,
        first_dance_song: firstDanceSong,
        total_price: price,
      });
      toast.success('DJ Booking submitted! We will contact you shortly.');
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { num: 1, label: 'Event Type' },
    { num: 2, label: 'Package' },
    { num: 3, label: 'Event Details' },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="dj-booking-page" style={{ background: 'linear-gradient(135deg, #0a0000 0%, #1a0508 25%, #0d0d0d 50%, #1a0508 75%, #0a0000 100%)' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#EDEDED]">
            Book <span style={{ color: '#EF0000' }}>J MAJOR</span>
          </h1>
          <p className="text-[#A1A1AA] text-sm mt-2">Fill in the details to secure your DJ</p>
        </div>

        {/* Success */}
        {step === 4 ? (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-16 bg-[#0a0a0a] border border-white/10 rounded-2xl">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(255,23,68,0.15)' }}>
              <Check className="w-8 h-8" style={{ color: '#EF0000' }} />
            </div>
            <h2 className="text-2xl font-['Playfair_Display'] text-[#EDEDED] mb-2">Booking Confirmed!</h2>
            <p className="text-[#A1A1AA] mb-6">We will be contacting you shortly to confirm your event.</p>
            <Button onClick={() => navigate('/dj')} className="rounded-full px-8" style={{ background: '#EF0000' }}>Back to DJ</Button>
          </motion.div>
        ) : (
          <>
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {steps.map((s, i) => (
                <div key={s.num} className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s.num ? 'text-white' : 'bg-[#1a1a1a] text-[#52525B]'
                  }`} style={step >= s.num ? { background: '#EF0000' } : {}}>
                    {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                  </div>
                  <span className={`text-xs hidden sm:block ${step >= s.num ? 'text-[#EDEDED]' : 'text-[#52525B]'}`}>{s.label}</span>
                  {i < steps.length - 1 && <div className={`w-8 h-px ${step > s.num ? 'bg-[#EF0000]' : 'bg-white/10'}`} />}
                </div>
              ))}
            </div>

            <div className="bg-[#0a0a0a]/80 border border-white/10 rounded-2xl p-6 md:p-8">
              <AnimatePresence mode="wait">
                {/* Step 1: Event Type */}
                {step === 1 && (
                  <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    <h2 className="text-xl text-[#EDEDED] font-medium mb-6">Select Your Event Type</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {EVENT_TYPES.map((et) => {
                        const Icon = et.icon;
                        return (
                          <button
                            key={et.value}
                            onClick={() => setEventType(et.value)}
                            className={`p-4 rounded-xl border text-center transition-all ${
                              eventType === et.value ? 'border-[#EF0000] bg-[#EF0000]/10' : 'border-white/10 hover:border-white/30'
                            }`}
                            data-testid={`dj-event-${et.value}`}
                          >
                            <Icon className="w-6 h-6 mx-auto mb-2" style={{ color: eventType === et.value ? '#EF0000' : '#52525B' }} />
                            <span className={`text-xs ${eventType === et.value ? 'text-[#EDEDED]' : 'text-[#A1A1AA]'}`}>{et.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    {eventType === 'other' && (
                      <div className="mt-4">
                        <label className="text-sm text-[#A1A1AA] block mb-2">Please specify your event type *</label>
                        <Input
                          value={customEventType}
                          onChange={e => setCustomEventType(e.target.value)}
                          placeholder="e.g. Anniversary Dinner, Graduation Party"
                          className="bg-[#111] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                          data-testid="dj-custom-event"
                        />
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Step 2: Package */}
                {step === 2 && (
                  <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    <h2 className="text-xl text-[#EDEDED] font-medium mb-2">Choose Your Package</h2>
                    <p className="text-xs text-[#A1A1AA] mb-6">Includes sound, complimentary toast song music, standard smoke machine LED, DJ Lights</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      {packages.map((pkg) => (
                        <button
                          key={pkg.tier}
                          onClick={() => setPackageTier(pkg.tier)}
                          className={`p-6 rounded-xl border text-left transition-all relative ${
                            packageTier === pkg.tier ? 'border-[#EF0000] bg-[#EF0000]/10' : 'border-white/10 hover:border-white/30'
                          }`}
                          data-testid={`dj-pkg-${pkg.tier}`}
                        >
                          {pkg.highlight && (
                            <span className="absolute -top-3 left-4 text-xs px-2 py-0.5 rounded-full text-white" style={{ background: '#EF0000' }}>Popular</span>
                          )}
                          <h3 className="text-lg font-semibold text-[#EDEDED] mb-1">{pkg.name}</h3>
                          <p className="text-3xl font-['Playfair_Display'] mb-1" style={{ color: '#EF0000' }}>£{pkg.price}</p>
                          <p className="text-xs text-[#52525B] mb-3">{pkg.duration}</p>
                          <ul className="space-y-1.5">
                            {pkg.features.map((f, i) => (
                              <li key={i} className="text-xs text-[#A1A1AA] flex items-center gap-2">
                                <Check className="w-3 h-3 flex-shrink-0" style={{ color: '#EF0000' }} />
                                {f}
                              </li>
                            ))}
                          </ul>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Event Details */}
                {step === 3 && (
                  <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    <h2 className="text-xl text-[#EDEDED] font-medium mb-6">Event Details</h2>
                    <div className="space-y-6">
                      {/* Date */}
                      <div>
                        <label className="text-sm text-[#A1A1AA] block mb-2">Event Date *</label>
                        <div className="flex justify-center bg-[#111] rounded-xl border border-white/10 p-3">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={setSelectedDate}
                            disabled={(date) => date < new Date()}
                            className="text-[#EDEDED]"
                          />
                        </div>
                      </div>

                      {/* Times */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-[#A1A1AA] block mb-2 flex items-center gap-2"><Clock className="w-4 h-4" /> Start Time *</label>
                          <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="bg-[#111] border-white/10 text-[#EDEDED]" data-testid="dj-start-time" />
                        </div>
                        <div>
                          <label className="text-sm text-[#A1A1AA] block mb-2 flex items-center gap-2"><Clock className="w-4 h-4" /> End Time</label>
                          <Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="bg-[#111] border-white/10 text-[#EDEDED]" data-testid="dj-end-time" />
                        </div>
                      </div>

                      {/* Location & Venue */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-[#A1A1AA] block mb-2 flex items-center gap-2"><MapPin className="w-4 h-4" /> Location</label>
                          <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. London" className="bg-[#111] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]" data-testid="dj-location" />
                        </div>
                        <div>
                          <label className="text-sm text-[#A1A1AA] block mb-2">Venue Name</label>
                          <Input value={venue} onChange={e => setVenue(e.target.value)} placeholder="e.g. O2 Academy" className="bg-[#111] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]" data-testid="dj-venue" />
                        </div>
                      </div>

                      {/* Guests & Phone */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-[#A1A1AA] block mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> Expected Guests</label>
                          <Input value={expectedGuests} onChange={e => setExpectedGuests(e.target.value)} placeholder="e.g. 100" className="bg-[#111] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]" data-testid="dj-guests" />
                        </div>
                        <div>
                          <label className="text-sm text-[#A1A1AA] block mb-2 flex items-center gap-2"><Phone className="w-4 h-4" /> Phone Number *</label>
                          <Input type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="+44 7123 456789" className="bg-[#111] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]" data-testid="dj-phone" />
                          <p className="text-xs mt-1" style={{ color: '#EF0000' }}>We will be contacting you shortly.</p>
                        </div>
                      </div>

                      {/* Music Preference */}
                      <div>
                        <label className="text-sm text-[#A1A1AA] block mb-3 flex items-center gap-2"><Music2 className="w-4 h-4" /> Music Preference</label>
                        <div className="flex flex-wrap gap-2">
                          {MUSIC_GENRES.map(genre => (
                            <button
                              key={genre}
                              onClick={() => toggleGenre(genre)}
                              className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                                musicPreferences.includes(genre)
                                  ? 'border-[#EF0000] text-white'
                                  : 'border-white/10 text-[#A1A1AA] hover:border-white/30'
                              }`}
                              style={musicPreferences.includes(genre) ? { background: 'rgba(255,23,68,0.2)' } : {}}
                              data-testid={`genre-${genre.toLowerCase().replace(/[^a-z]/g, '-')}`}
                            >
                              {genre}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Let DJ Choose */}
                      <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-white/10 hover:border-white/20">
                        <input
                          type="checkbox"
                          checked={letDJChoose}
                          onChange={e => setLetDJChoose(e.target.checked)}
                          className="w-4 h-4 accent-[#EF0000]"
                          data-testid="let-dj-choose"
                        />
                        <span className="text-sm text-[#A1A1AA]">Not sure — let J Major choose</span>
                      </label>

                      {/* Special Request */}
                      <div>
                        <label className="text-sm text-[#A1A1AA] block mb-2">Special Request</label>
                        <textarea
                          value={specialRequest}
                          onChange={e => setSpecialRequest(e.target.value.slice(0, 1000))}
                          placeholder="Tell us any must-play songs, artists or genres you'd like J Major to include"
                          rows={3}
                          maxLength={1000}
                          className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2 text-[#EDEDED] text-sm placeholder:text-[#52525B] resize-none"
                          data-testid="dj-special-request"
                        />
                        <p className="text-xs text-[#52525B] text-right mt-1">{specialRequest.length}/1000</p>
                      </div>

                      {/* First Dance Song */}
                      <div>
                        <label className="text-sm text-[#A1A1AA] block mb-2">Do you have a first dance or entrance song?</label>
                        <Input
                          value={firstDanceSong}
                          onChange={e => setFirstDanceSong(e.target.value)}
                          placeholder="e.g. Ed Sheeran - Perfect"
                          className="bg-[#111] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                          data-testid="dj-first-dance"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                {step > 1 ? (
                  <Button onClick={() => setStep(s => s - 1)} variant="outline" className="border-white/10 text-[#A1A1AA] rounded-full">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                ) : <div />}
                {step < 3 ? (
                  <Button
                    onClick={() => setStep(s => s + 1)}
                    disabled={!canProceed()}
                    className="rounded-full text-white"
                    style={{ background: canProceed() ? '#EF0000' : '#333' }}
                    data-testid="dj-next-step"
                  >
                    Continue <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={!canProceed() || submitting}
                    className="rounded-full text-white"
                    style={{ background: canProceed() ? '#EF0000' : '#333' }}
                    data-testid="dj-submit"
                  >
                    {submitting ? 'Submitting...' : 'Confirm Booking'}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
